export const DEFAULT_PROFILE_LIMIT = 5;

export const COMPLETION_THRESHOLD = 0.92;
export const MIN_RESUME_SECONDS = 30;

/** Avatars by theme, in the order the picker shows them. */
export const AVATAR_THEMES = {
  animals: [
    'raccoon',
    'otter',
    'panda',
    'koala',
    'red-panda',
    'fox',
    'bunny',
    'penguin',
    'cat',
    'owl',
    'hedgehog',
    'sloth',
  ],
  heroes: [
    'hero-bolt',
    'hero-blaze',
    'hero-frost',
    'hero-nova',
    'hero-shade',
    'hero-titan',
    'hero-comet',
    'hero-bloom',
  ],
  movies: ['rock-alien', 'astronaut', 'starship', 'dinosaur', 'wizard', 'robot', 'shark', 'pirate'],
  series: [
    'chemist',
    'mastermind',
    'desert-rv',
    'dragon',
    'zombie',
    'detective',
    'monarch',
    'knight',
  ],
} as const;
export type AvatarTheme = keyof typeof AVATAR_THEMES;

export const AVATAR_IDS = [
  ...AVATAR_THEMES.animals,
  ...AVATAR_THEMES.heroes,
  ...AVATAR_THEMES.movies,
  ...AVATAR_THEMES.series,
] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

/** Avatars from before 0.5.18, still sent by older apps and stored on older profiles. */
export const LEGACY_AVATARS = {
  teacup: 'otter',
  moon: 'owl',
  blossom: 'bunny',
  comet: 'hero-comet',
  wave: 'shark',
  star: 'hero-nova',
  cloud: 'koala',
  heart: 'red-panda',
  leaf: 'sloth',
  planet: 'astronaut',
  spark: 'hero-bolt',
  frog: 'dinosaur',
  ghost: 'zombie',
  rocket: 'starship',
  sun: 'hero-blaze',
  mountain: 'hero-titan',
  music: 'rock-alien',
  crown: 'monarch',
} as const satisfies Record<string, AvatarId>;

export const DEFAULT_AVATAR: AvatarId = 'otter';

/** Any stored or sent avatar id as one of today's. */
export function normalizeAvatar(id: string): AvatarId {
  if ((AVATAR_IDS as readonly string[]).includes(id)) return id as AvatarId;
  return LEGACY_AVATARS[id as keyof typeof LEGACY_AVATARS] ?? DEFAULT_AVATAR;
}

export const AVATAR_IMAGE_MAX_BYTES = 300_000;

export const MATURITY_LEVELS = [
  { rank: 0, label: 'All ages', short: 'G' },
  { rank: 1, label: 'Older kids', short: 'PG' },
  { rank: 2, label: 'Teens', short: '13+' },
  { rank: 3, label: 'Mature', short: '16+' },
  { rank: 4, label: 'All maturity ratings', short: '18+' },
] as const;
export const MAX_MATURITY_RANK = 4;
export const KIDS_DEFAULT_MATURITY = 1;
export const UNKNOWN_CERT_RANK = 3;

const CERT_RANKS: Record<string, number> = {
  G: 0,
  PG: 1,
  'PG-13': 2,
  R: 3,
  'NC-17': 4,
  NR: 3,
  'TV-Y': 0,
  'TV-G': 0,
  'TV-Y7': 1,
  'TV-Y7-FV': 1,
  'TV-PG': 1,
  'TV-14': 2,
  'TV-MA': 3,
  U: 0,
  UC: 0,
  '12': 2,
  '12A': 2,
  '15': 3,
  '16': 3,
  '18': 4,
  R18: 4,
};

export function certificationRank(cert: string | null | undefined): number | null {
  if (!cert) return null;
  const rank = CERT_RANKS[cert.trim().toUpperCase()];
  return rank ?? null;
}

export function isAllowedForMaturity(
  cert: string | null | undefined,
  limit: number,
  adult = false,
): boolean {
  if (adult && limit < MAX_MATURITY_RANK) return false;
  if (limit >= MAX_MATURITY_RANK) return true;
  const rank = certificationRank(cert) ?? UNKNOWN_CERT_RANK;
  return rank <= limit;
}

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ar', label: 'العربية' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'pl', label: 'Polski' },
] as const;
